import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { Subject, filter, takeUntil } from 'rxjs';

import { TranslationService } from '@mirage-projects/cw-commons';
import { LayerType, PcvizJobAdapter } from '@mirage-projects/cw-models';
import { JobPcvizService } from '@mirage-projects/cw-pcviz-core';

import { UploadWizardService } from '../../../services/upload-wizard-service/upload-wizard.service';

interface PointcloudConversionSelectorOption {
    label: string;
    value: number;
}

export interface PointcloudConversionOptions {
    config: number;
}

/**
 * Poincloud import options
 * This component is used as an optional step in the layer creation to set the pointclould options
 */
@Component({
    selector: 'cw-pointcloud-import-options',
    templateUrl: 'pointcloud-import-options.component.html',
    standalone: false,
})
export class PointcloudImportOptionsComponent implements OnInit, OnDestroy {
    /** Text that will be shown as a definition of the wizard in all the steps */
    @Input() subtitle = '';

    tabName = 'WIZARD.TAB_CONVERT_POINTCLOUD';
    qualityMessage = '';
    pointcloudData: PointcloudConversionOptions = this.uploadWizardService.pointcloudConversion;
    onDestroy$: Subject<void> = new Subject();
    options: PointcloudConversionSelectorOption[] = [
        { label: 'UPLOAD.POINTCLOUD_QUALITY_STANDARD', value: 0 },
        { label: 'UPLOAD.POINTCLOUD_QUALITY_BEST', value: 1 },
    ];

    constructor(
        private uploadWizardService: UploadWizardService,
        private jobAdapterService: JobPcvizService,
        private translationService: TranslationService
    ) {}

    ngOnInit(): void {
        this.updateQualityMessage();
        this.listenToStepChanged();
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    change(): void {
        this.updateQualityMessage();
    }

    private listenToStepChanged(): void {
        this.uploadWizardService.stepChanged
            .pipe(
                takeUntil(this.onDestroy$),
                filter((wizardComponent) => {
                    const step = this.translationService.translate(this.tabName);
                    return this.uploadWizardService.comparePreviousStep(wizardComponent, step);
                })
            )
            .subscribe((wizardComponent) => {
                this.updateSummary(wizardComponent.selectedIndex);
                this.updateProcessingOptions();
            });
    }

    private updateProcessingOptions(): void {
        const jobAdapter: PcvizJobAdapter | undefined = this.jobAdapterService.getJobAdapter();
        if (!jobAdapter) {
            return;
        }

        jobAdapter._processingOptions = { bestsampling: this.pointcloudData.config };
    }

    private updateSummary(summaryPosition: number): void {
        const dataType: LayerType = this.jobAdapterService.getJobAdapter()?.getLayerType() ?? 'PointCloud';
        const isPointCloud = this.uploadWizardService.isPointCloud();

        if (isPointCloud) {
            this.uploadWizardService.summary[summaryPosition] = {
                dataType,
                title: 'WIZARD.TAB_CONVERT_POINTCLOUD',
                fields: [{ key: 'UPLOAD.POINTCLOUD_QUALITY_SELECT', value: this.qualityMessage }],
            };
        } else {
            const obsoleteData = this.uploadWizardService.summary[summaryPosition].dataType === 'PointCloud';

            if (obsoleteData) {
                this.uploadWizardService.summary[summaryPosition] = {};
            }
        }
    }

    private updateQualityMessage(): void {
        this.qualityMessage =
            this.pointcloudData.config === this.options[0].value
                ? 'UPLOAD.POINTCLOUD_QUALITY_MSG_STANDARD'
                : 'UPLOAD.POINTCLOUD_QUALITY_MSG_HIGH';
    }
}
